import { useEffect, useRef } from 'react';

/**
 * Makes the browser/Android Back button close an overlay instead of leaving the
 * screen.
 *
 * On a phone the details panel, the thread column and the master/detail panes
 * cover the whole viewport but do not change the URL — they are component
 * state. Without this, Back on an open details panel navigates away from the
 * conversation entirely, which reads as the app throwing the user out.
 *
 * The mechanism is one synthetic history entry per open overlay:
 *
 *   open   → push an entry at the SAME url, tagged with an id
 *   Back   → popstate fires, `onBack` closes the overlay, our entry is gone
 *   close  → the entry is still on top, so it is popped to keep the stack level
 *
 * The pushed state is a clone of whatever is already there, so React Router's
 * own bookkeeping (`idx`, `key`, `usr`) survives the push — replacing it
 * outright leaves the router unable to place the entry it gets back on pop.
 *
 * Prefer ONE guard per screen over one per panel: `activeKey` says WHICH layer
 * is on top and `onBack` peels it. Guards nested one per panel have to unwind
 * and re-push whenever the panels swap, which happens in a single render and is
 * the awkward case; one guard that re-arms per layer does not.
 *
 * @param activeKey falsy when nothing is guarded. Otherwise any value
 *   identifying the layer currently on top — the guard re-arms whenever it
 *   changes, which is what puts a fresh entry on the stack after Back has
 *   consumed the previous one and revealed the layer beneath.
 */

/**
 * Overlays can nest, and `popstate` is a window event every mounted guard would
 * otherwise hear, so one Back would close the whole stack at once. Guards are
 * kept in a shared stack and only the top one answers.
 */
const guardStack = [];

/**
 * Pops this module asked for itself, balancing an entry it pushed when an
 * overlay was closed from inside the app.
 *
 * These have to be told apart from the user pressing Back. `history.back()` is
 * asynchronous: if another guard mounts in the same commit — closing one panel
 * and opening another is a single render — it pushes its entry before the pop
 * we requested is delivered, and that pop would otherwise be read as a Back
 * press and close the panel that had just opened.
 */
let pendingSelfPop = 0;

const onPopState = () => {
  if (pendingSelfPop > 0) {
    pendingSelfPop -= 1;
    return;
  }

  const top = guardStack.pop();
  if (!top) return;

  // Its entry is the one the browser just discarded, so there is nothing left
  // for the teardown to pop.
  top.consumed = true;
  top.onBack();
};

/**
 * One listener for the life of the page. It is never removed: teardown has to
 * stay subscribed long enough to hear the pop it requested, and the handler is
 * inert whenever nothing is guarded.
 */
let listening = false;
const listen = () => {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('popstate', onPopState);
  listening = true;
};

let nextGuardId = 1;

export const useBackGuard = (activeKey, onBack) => {
  // Held in a ref so a new inline `onBack` on every render cannot tear the
  // guard down and push a second history entry.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  /**
   * Distinguishes "the overlay closed" from "this screen went away".
   *
   * The teardown below balances the history stack by popping the entry it
   * pushed. That is right when an overlay is dismissed and the screen under it
   * stays — and badly wrong when the whole tree is unmounting, because
   * `history.back()` is asynchronous and lands AFTER whatever navigation caused
   * the unmount, dragging the user back to the screen they just left.
   *
   * Signing out was exactly that: logout swaps the route tree, the guard on the
   * settings pane unmounted, and its queued `back()` overrode the redirect to
   * the login page — leaving the URL on /settings/security with a signed-out
   * app rendering nothing at it.
   *
   * Declared BEFORE the guard effect so that on unmount React runs this cleanup
   * first; cleanups fire in declaration order, so the flag is already set by the
   * time the guard's own cleanup reads it. On an ordinary `activeKey` change
   * this effect does not re-run at all, so the flag stays false and the pop
   * still happens.
   */
  const unmounting = useRef(false);
  useEffect(
    () => () => {
      unmounting.current = true;
    },
    []
  );

  useEffect(() => {
    if (!activeKey || typeof window === 'undefined') return undefined;

    const id = nextGuardId++;
    const entry = { id, consumed: false, onBack: () => onBackRef.current?.() };

    try {
      window.history.pushState({ ...window.history.state, synapseOverlay: id }, '');
    } catch {
      // History is unavailable (sandboxed frame). The in-app close control is
      // still there, so degrade to no interception rather than failing.
      return undefined;
    }

    listen();
    guardStack.push(entry);

    return () => {
      const index = guardStack.indexOf(entry);
      if (index !== -1) guardStack.splice(index, 1);

      // Its entry was already discarded by the Back press that closed it.
      if (entry.consumed) return;

      // The screen itself is going away — see `unmounting`. Whatever navigation
      // is taking it away owns the history stack from here.
      if (unmounting.current) return;

      // Closed from inside the app. Drop the synthetic entry so open/close
      // cycles do not pile up — but only while it is still the one on top: a
      // real navigation over the overlay (following a notification to its
      // conversation) owns the top entry, and popping that would undo it.
      if (window.history.state?.synapseOverlay === id) {
        pendingSelfPop += 1;
        window.history.back();
      }
    };
  }, [activeKey]);
};

export default useBackGuard;
